import { getPersonType, PersonType, QUERY_RESPONSE_PERSON_TYPES } from "@whatslly/types/Person";
import { SelectedContactType } from "@whatslly/types/SelectedContactType";
import { stripNumberStringToOnlyNumbers, getPrettyPhone } from "@whatslly/commons/utils";

const PREFIX = "BEGIN:VCARD";
const POSTFIX = "END:VCARD";

const splitNameToFirstAndLast = (contactName: string | undefined, fallbackName: string | undefined) => {
  if (!contactName) {
    return { firstName: fallbackName, lastName: "" };
  }
  if (contactName.includes(" ")) {
    const personNameParts = contactName.split(" ");
    if (personNameParts.length === 2) {
      return {
        firstName: personNameParts[0].trim(),
        lastName: personNameParts[personNameParts.length - 1].trim(),
      };
    }
    return {
      firstName: personNameParts[0].trim(),
      lastName: personNameParts[1].trim(),
    };
  }
  return { firstName: contactName, lastName: "" };
};

export const vCardStringBuilder = ({
  person,
  selectedContact,
  org,
  entityLink,
}: {
  person: PersonType | PersonType[] | undefined;
  selectedContact: SelectedContactType | undefined;
  org: string | undefined;
  entityLink: string | undefined;
}) => {
  if (!person || !selectedContact) {
    return "";
  }

  if (Array.isArray(person) || getPersonType(person) === QUERY_RESPONSE_PERSON_TYPES.UNKNOWN) {
    const { name: contactName, phone: contactPhone } = selectedContact;

    const contactNameOrPhone = contactName ?? contactPhone;

    if (!contactNameOrPhone) {
      return "";
    }
    const contactNameIsOnlyNumber = stripNumberStringToOnlyNumbers(contactNameOrPhone)
      ? !Number.isNaN(Number(stripNumberStringToOnlyNumbers(contactNameOrPhone)))
      : false;
    const { firstName, lastName } = contactNameIsOnlyNumber
      ? { firstName: contactPhone, lastName: "" }
      : splitNameToFirstAndLast(contactName, contactPhone);

    return (
      `${PREFIX}\n` +
      "VERSION:4.0\n" +
      `N:${lastName ? lastName : firstName};${lastName ? firstName : ""};;;\n` +
      `FN:${contactName}\n` + //+86(13)694-991481
      `TEL;TYPE=cell:${getPrettyPhone(contactPhone ?? "")}\n` + //+8613694991481
      `${POSTFIX}\n`
    );
  }
  const { originalPhone, originalCaller, Title } = person;
  if (!originalPhone) {
    return "";
  }
  const { firstName, lastName } = splitNameToFirstAndLast(person.Name || originalCaller, originalPhone);

  const fullName = (() => {
    const personName = person.Name || originalCaller;
    if (!personName) {
      return originalPhone;
    }
    return Title ? `${Title} ${personName}` : personName;
  })();

  const email = person.Email || person.PersonEmail;

  let vcard =
    "BEGIN:VCARD\n" +
    "VERSION:4.0\n" +
    `N:${lastName ? lastName : firstName};${lastName ? firstName : ""};;${Title ? Title : ""};\n` +
    `FN:${fullName}\n` +
    `TEL;TYPE=cell:${getPrettyPhone(person.originalPhone ?? "")}\n`;

  if (org) {
    vcard = `${vcard}ORG:${org}\n`;
  }

  if (entityLink) {
    vcard = `${vcard}URL:${entityLink}\n`;
  }

  if (email) {
    vcard = `${vcard}EMAIL:${email}\n`;
  }

  return `${vcard}END:VCARD\n`;
};

const HAS_SEMICOLON_SEPARATOR = /[^\\];|^;/;
const HAS_COMMA_SEPARATOR = /[^\\],|^,/;

/**
 * Split vcard field value by separator
 * @param {string|string[]} value
 * @param {string} separator
 * @returns {string|string[]}
 */
function splitValue(value: string, separator: string) {
  const separatorRegexp = new RegExp(separator);
  const escapedSeparatorRegexp = new RegExp(`\\\\${separator}`, "g");
  // easiest way, replace it with really rare character sequence
  let cloneValue: any = value;
  cloneValue = cloneValue.replace(escapedSeparatorRegexp, "ΩΩΩ");
  if (value.match(separatorRegexp)) {
    cloneValue = cloneValue.split(separator);

    cloneValue = cloneValue.map((item: string) => {
      return item.replace(/ΩΩΩ/g, separator);
    });
  } else {
    cloneValue = cloneValue.replace(/ΩΩΩ/g, separator);
  }
  return cloneValue;
}

/**
 * Split value by "," or ";" and remove escape sequences for this separators
 * @param {string} value
 * @returns {string|string[]
 */
function tryToSplit(value: string) {
  if (value.match(HAS_SEMICOLON_SEPARATOR)) {
    value = value.replace(/\\,/g, ",");
    return splitValue(value, ";");
  } else if (value.match(HAS_COMMA_SEPARATOR)) {
    value = value.replace(/\\;/g, ";");
    return splitValue(value, ",");
  } else {
    return value.replace(/\\,/g, ",").replace(/\\;/g, ";");
  }
}

//https://www.npmjs.com/package/vcard-parser
export const vcardParser = (string: string) => {
  try {
    const result: Record<string, any> = {};
    const lines = string.split(/\r\n|\r|\n/);
    const count = lines.length;
    let pieces;
    let key;
    let value;
    let meta: { [x: string]: string[] };
    let namespace;

    for (let i = 0; i < count; i++) {
      if (lines[i] === "") {
        continue;
      }
      if (lines[i].toUpperCase() === PREFIX || lines[i].toUpperCase() === POSTFIX) {
        continue;
      }
      let data = lines[i];

      /**
       * Check that next line continues current
       * @param {number} i
       * @returns {boolean}
       */
      const isValueContinued = (i: number) => {
        return i + 1 < count && (lines[i + 1][0] === " " || lines[i + 1][0] === "\t");
      };
      // handle multiline properties (i.e. photo).
      // next line should start with space or tab character
      if (isValueContinued(i)) {
        while (isValueContinued(i)) {
          data += lines[i + 1].trim();
          i++;
        }
      }

      pieces = data.split(":");
      key = pieces.shift();
      value = pieces.join(":");
      namespace = false;
      meta = {};

      // meta fields in property
      if (key?.match(/;/)) {
        key = key.replace(/\\;/g, "ΩΩΩ").replace(/\\,/, ",");
        const metaArr = key.split(";").map(function (item) {
          return item.replace(/ΩΩΩ/g, ";");
        });
        key = metaArr.shift();
        metaArr.forEach(function (item) {
          const arr = item.split("=");
          arr[0] = arr[0].toLowerCase();
          if (arr[0].length === 0) {
            return;
          }
          if (meta[arr[0]]) {
            meta[arr[0]].push(arr[1]);
          } else {
            meta[arr[0]] = [arr[1]];
          }
        });
      }

      // values with \n
      value = value.replace(/\\n/g, "\n");

      value = tryToSplit(value);

      // Grouped properties
      if (key?.match(/\./)) {
        const arr = key.split(".");
        key = arr[1];
        namespace = arr[0];
      }

      const newValue: Record<string, any> = {
        value: value,
      };
      if (Object.keys(meta).length) {
        newValue.meta = meta;
      }
      if (namespace) {
        newValue.namespace = namespace;
      }

      if (key && key.indexOf("X-") !== 0) {
        key = key?.toLowerCase();
      }

      if (key) {
        if (typeof result[key] === "undefined") {
          result[key] = [newValue];
        } else {
          result[key].push(newValue);
        }
      }
    }

    return result;
  } catch {
    return undefined;
  }
};
